import * as React from "react";

import { DownOutlined } from "@ant-design/icons";
import {
    ColumnBuilder,
    ColumnOptions,
} from "@impero-com/common/components/dataTable";
import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { TableFilter } from "@impero-com/common/components/filter";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    DataViewIndex,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    actionMenuStatus,
    Button,
    ContentWithoutBorder,
    PageSpinner,
    renderMarkdown,
} from "@impero-com/ui";
import { Dropdown, Menu } from "antd";
import memoizeOne from "memoize-one";

import {
    BinaryNodeScope,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    EntityOverviewListDto,
    EntityOverview,
    SavedBlob,
} from "api/generated/model";
import { Mode, ModeType, SwitchableTreeView } from "components/switchable";
import IconDelete from "img/delete.svg";
import IconDuplicate from "img/duplicate.svg";
import IconEdit from "img/edit.svg";
import IconRiskMap from "img/riskmap.svg";
import shareIcon from "img/share-icon.svg";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import {
    EntityId,
    MetadataColumnId,
    RiskNodeId,
} from "org/pageState/convert/risks/common";
import {
    pageStateRiskDirectoryCodecs,
    RiskDirPageStateRaw,
} from "org/pageState/convert/risks/riskDirectories";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { splitPageStateWithCallback } from "org/pageState/utils";
import { CreateNodeDrawer } from "org/risks/content/createNodeDrawer";
import { DirectoryNodeDrawer } from "org/risks/content/directoryDrawer";
import * as R from "org/risks/types";
import {
    ENTITY_EXTRACTOR_KEYS,
    ProcessNode,
    RISK_EXTRACTOR_KEYS,
} from "org/risks/types";
import { RISK_MODULE, RISK_DIRECTORY_STRUCTURE } from "org/risks/url";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { NameColumn } from "org/risks/widgets/nameColumn";
import { downgradeExtractorType } from "utils/dataView";
import { Guid, generate } from "utils/guid";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { FormulaComputer } from "../formulaComputer";
import { EnumMap } from "../impactLikelihood";
import { computeArgsForSwitchableTreeView } from "../map";
import * as theme from "../theme.css";
import * as T from "../types";
import * as U from "../utils";

import { CopyWithConfirmationDialog } from "./dialog";

export class DirectoryPage extends React.PureComponent<
    DirectoryTabProps,
    DirectoryViewState
> {
    constructor(props: DirectoryTabProps) {
        super(props);

        this.state = {
            dialog: undefined,
            drawer: undefined,
        };
    }

    nodeTypeToNodeExtractors = memoizeOne(
        (riskDirectory: R.RiskDirectoryView, savedBlobs: SavedBlob[]) =>
            this.buildNodeTypeToNodeExtractors(riskDirectory, savedBlobs)
    );
    directoryDataView = memoizeOne(
        (riskDirectory: R.RiskDirectoryView, rootNode: RiskDirectoryNodeDto) =>
            this.buildDirectoryDataView(riskDirectory, rootNode)
    );
    directoryQueryResult = memoizeOne(
        (
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            filter: SimpleAndFilter | null,
            entityList: EntityOverviewListDto
        ) =>
            this.buildDirectoryQueryResult(
                riskDirectory,
                rootNode,
                filter,
                entityList
            )
    );
    directoryDataViewIndex = memoizeOne(
        (
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            entityList: EntityOverviewListDto
        ) =>
            this.buildDirectoryDataViewIndex(
                riskDirectory,
                rootNode,
                entityList
            )
    );
    directoryColumnBuilders = memoizeOne((riskDirectory: R.RiskDirectoryView) =>
        this.buildDirectoryColumnBuilders(riskDirectory)
    );
    directoryDrawerNode = memoizeOne(
        (
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            nodeId: Guid
        ) => {
            const dataView = this.directoryDataView(riskDirectory, rootNode);
            return DV.find(
                dataView.dataSource.iter(),
                (desc) => dataView.dataSource.getId(desc) === nodeId
            );
        }
    );
    nodeCreationPermission = memoizeOne(
        (riskDirectory: R.RiskDirectoryView, rootNode: RiskDirectoryNodeDto) =>
            this.buildNodeCreationPermission(riskDirectory, rootNode)
    );
    entityDataViewIndex = memoizeOne((entityList: EntityOverviewListDto) =>
        this.buildEntityDataViewIndex(entityList)
    );

    render = () => {
        const { drawer } = this.state;
        const {
            riskDirectoryView,
            rootNode,
            entityList,
            common,
            lastAddedNodeId,
        } = this.props;
        const dataViewIndex = this.directoryDataViewIndex(
            riskDirectoryView,
            rootNode,
            entityList
        );
        const processMetadataColumns = common
            .metadataColumns(riskDirectoryView)
            .riskDirectoryLevel.filter(
                (col) =>
                    col.fields.Process !== undefined &&
                    col.fields.Process.fieldType.type !== "TextArea"
            );
        const riskMetadataColumns = common
            .metadataColumns(riskDirectoryView)
            .riskDirectoryLevel.filter(
                (col) =>
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
            );
        const columnBuilders = this.directoryColumnBuilders(riskDirectoryView);

        const nodeTypeToNodeExtractors = this.nodeTypeToNodeExtractors(
            riskDirectoryView,
            rootNode.linkedResources.blobs
        );

        const renderError = (code: number | undefined) =>
            this.withApplicationBar(
                undefined,
                code === 403 ? (
                    <NoAccessCard />
                ) : (
                    <LoggedInHTTPError code={code} />
                )
            );
        const renderLoading = () => <PageSpinner />;

        const colsByLevel = common.metadataColumns(riskDirectoryView);
        const customColumns = colsByLevel.riskDirectoryLevel.concat(
            colsByLevel.entityLevel
        );

        return (
            <PageStateManager<"RiskDirectory", RiskDirPageStateRaw>
                pageKey={PAGE_KEY.riskDirectory(
                    riskDirectoryView.content.resource.id
                )}
                codecs={pageStateRiskDirectoryCodecs(customColumns)}
                renderLoading={renderLoading}
                renderError={renderError}
                renderContent={({
                    pageState,
                    onPageStateChange,
                    onCreateShareableLink,
                }) => {
                    const {
                        filter,
                        onFilterChange,
                        visibleColumnIds,
                        onVisibleColumnsChange,
                    } = splitPageStateWithCallback(
                        pageState,
                        onPageStateChange
                    );
                    const queryResult = this.directoryQueryResult(
                        riskDirectoryView,
                        rootNode,
                        filter,
                        entityList
                    );

                    const {
                        switchableViewMode,
                        onSwitchableViewModeChange,
                        visibleColumnIdsForTableView,
                        onVisibleColumnsForTableViewChanged,
                        visibleColumnIdsForTreeView,
                        onVisibleColumnsForTreeViewChanged,
                    } = computeArgsForSwitchableTreeView(
                        pageState,
                        onPageStateChange,
                        visibleColumnIds,
                        onVisibleColumnsChange
                    );

                    return this.withApplicationBar(
                        onCreateShareableLink,
                        <ContentWithoutBorder>
                            {this.renderDialog()}
                            <TableFilter<DV.NodeDescriptor<T.DirectoryNode>>
                                id="risk_directory_filter"
                                hideResultCount
                                extractorRefs={[
                                    { extractorId: RISK_EXTRACTOR_KEYS.RISK },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.PROCESSES,
                                    },
                                    ...processMetadataColumns.map((col) => ({
                                        extractorId:
                                            U.prefixFilterMetadataExtractor(
                                                col.id,
                                                "Process"
                                            ),
                                    })),
                                    ...riskMetadataColumns.map((col) => ({
                                        extractorId:
                                            U.prefixFilterMetadataExtractor(
                                                col.id,
                                                "Risk"
                                            ),
                                    })),
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.ENTITIES,
                                    },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
                                    },
                                ]}
                                onAddFilter={this.addFilter(
                                    filter,
                                    onFilterChange
                                )}
                                onRemoveFilter={this.removeFilter(
                                    filter,
                                    onFilterChange
                                )}
                                onResetFilter={this.resetFilter(
                                    filter,
                                    onFilterChange
                                )}
                                queryResult={queryResult}
                                dataViewIndex={dataViewIndex}
                                filter={filter}
                            />
                            <SwitchableTreeView
                                id="risk_directory_view"
                                dataView={queryResult.dataView}
                                visibleColumnIdsForTreeView={
                                    visibleColumnIdsForTreeView
                                }
                                onVisibleColumnsForTreeViewChanged={
                                    onVisibleColumnsForTreeViewChanged
                                }
                                visibleColumnIdsForTableView={
                                    visibleColumnIdsForTableView
                                }
                                onVisibleColumnsForTableViewChanged={
                                    onVisibleColumnsForTableViewChanged
                                }
                                defaultMode={switchableViewMode}
                                onModeChange={onSwitchableViewModeChange}
                                dataViewModifier={{
                                    [Mode.TABLE]: (dataView) => {
                                        const filteredDataSource =
                                            new DV.ArrayDataSource(
                                                DV.toArray(
                                                    dataView.dataSource.iter()
                                                ),
                                                (n) =>
                                                    dataView.dataSource.getId(
                                                        n
                                                    ),
                                                [(n) => n.value.type === "Risk"]
                                            );
                                        return {
                                            ...dataView,
                                            dataSource: filteredDataSource,
                                        };
                                    },
                                }}
                                openRoots
                                lastAddedNodeId={lastAddedNodeId}
                                columnBuilders={columnBuilders}
                                rowClickHandler={{
                                    type: "ActiveRowClickHandler",
                                    activeRowId:
                                        drawer && drawer.type === "View"
                                            ? drawer.nodeId
                                            : undefined,
                                    onClick: this.handleActiveNodeChanged,
                                }}
                                getAddChildWidget={this.getAddNodeWidget}
                                maxHeight={500}
                            />
                            {this.renderViewDrawer(nodeTypeToNodeExtractors)}
                            {this.renderCreateDrawer(nodeTypeToNodeExtractors)}
                        </ContentWithoutBorder>
                    );
                }}
            />
        );
    };

    withApplicationBar = (
        onCreateShareableLink: (() => void) | undefined,
        children: React.ReactNode
    ) => {
        const { riskDirectoryView, rootNode } = this.props;

        const atLeastOneOpenValidityInterval =
            rootNode.content.resource.riskMapValidity.some(
                (validity) => validity.start !== null && validity.end === null
            );

        const actions = [
            {
                id: "risk_directory_copy_action",
                label: t("RiskDirectory_View_Action_CopyDirectory"),
                icon: { svg: IconDuplicate },
                status: actionMenuStatus(
                    riskDirectoryView.content.actions.copy,
                    t("RiskDirectory_Action_Copy_Forbidden")
                ),
                onClick: () => {
                    this.setState({ dialog: "CopyDirectory" });
                },
            },
            {
                id: "risk_directory_delete_action",
                label: t("RiskDirectory_View_Action_DeleteDirectory"),
                icon: { svg: IconDelete },
                status: actionMenuStatus(
                    riskDirectoryView.content.actions.delete,
                    t("RiskDirectory_Action_Delete_Forbidden")
                ),
                onClick: () => {
                    this.setState({ dialog: "DeleteDirectory" });
                },
            },
            {
                id: "risk_directory_settings_action",
                label: t("RiskDirectory_View_Action_Settings"),
                icon: { svg: IconEdit },
                link: {
                    to: `${RISK_MODULE}/${this.props.riskDirectoryView.content.resource.id}${RISK_DIRECTORY_STRUCTURE}`,
                },
            },
            {
                id: "risk_directory_go_to_riskmap_action",
                label: t("RiskDirectory_View_Action_GoToRiskmap"),
                icon: { svg: IconRiskMap },
                status: actionMenuStatus(
                    atLeastOneOpenValidityInterval,
                    t("RiskManager_NoEntitiesAssignedYet")
                ),
                link: { to: `#${T.RISK_MAP_TAB}` },
            },
        ];
        const actionsWithShareableLink = onCreateShareableLink
            ? [
                  {
                      id: "shareable-link-create-new",
                      label: t("ShareableLink_CreateNew"),
                      icon: { svg: shareIcon },
                      onClick: onCreateShareableLink,
                  },
                  ...actions,
              ]
            : actions;

        return (
            <OrgApplicationBar
                title={{
                    title: riskDirectoryView.content.resource.title,
                    breadCrumbs: [
                        {
                            label: t("RiskDirectory_RiskManager"),
                            url: RISK_MODULE,
                        },
                    ],
                }}
                actions={{
                    id: "risk_directory_actions",
                    actions: actionsWithShareableLink,
                }}
            >
                {children}
            </OrgApplicationBar>
        );
    };
    renderViewDrawer = (
        nodeTypeToNodeExtractors: Record<
            "Process" | "Risk",
            DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
        >
    ) => {
        if (this.state.drawer?.type === "View") {
            const {
                riskDirectoryView,
                entityList,
                rootNode,
                common,
                updateNode,
                onDeleteNode,
                ajaxManager,
                updateRootNodeLinkedResources,
            } = this.props;
            const { nodeId } = this.state.drawer;
            const node = this.directoryDrawerNode(
                riskDirectoryView,
                rootNode,
                nodeId
            );
            if (!node) {
                console.error("Cannot find drawer node " + nodeId);
                this.setState({ drawer: undefined });
                return;
            }
            let metadataExtractors: DV.DataExtractor<
                T.NodeWithMD,
                T.MetadataInstance
            >[];
            switch (node.value.type) {
                case "Risk":
                case "Process":
                    metadataExtractors =
                        nodeTypeToNodeExtractors[node.value.type];
                    break;
                case "RiskDirectory":
                    // No metadata on the risk directory
                    metadataExtractors = [];
                    break;
                default:
                    return assertNever(node.value);
            }
            let ancestorsEntities: Record<number, string>;
            if (node.ancestors.length > 0) {
                ancestorsEntities =
                    common.getDirectoryAncestorsEntitiesWithNodePaths(
                        node.ancestors
                    );
            } else {
                ancestorsEntities = [];
            }
            const nodeEntities: number[] = common.getComputedNodeEntityIds(
                node.value.id
            );
            const descendantsEntities: Record<number, string[]> =
                common.getDirectoryDescendantsEntitiesWithNodePaths(node);
            const canEditNodeOrDeleteEntityLink =
                rootNode.content.actions.editRiskDirectoryFields.indexOf(
                    node.value.id
                ) >= 0;
            return (
                <DirectoryNodeDrawer
                    riskDirectoryView={riskDirectoryView}
                    nodeDescriptor={node}
                    ajaxManager={ajaxManager}
                    ancestorsEntities={ancestorsEntities}
                    nodeEntities={nodeEntities}
                    descendantsEntities={descendantsEntities}
                    metadataExtractors={metadataExtractors}
                    updateNode={updateNode}
                    onDeleteNode={onDeleteNode}
                    entities={common.entities(entityList, rootNode)}
                    canEditNode={canEditNodeOrDeleteEntityLink}
                    canDeleteEntityLink={canEditNodeOrDeleteEntityLink}
                    canDeleteNode={
                        rootNode.content.actions.delete.indexOf(
                            node.value.id
                        ) >= 0
                    }
                    onClose={this.handleCloseDrawer}
                    updateRootNodeLinkedResources={
                        updateRootNodeLinkedResources
                    }
                />
            );
        } else {
            return null;
        }
    };

    renderCreateDrawer = (
        nodeTypeToNodeExtractors: Record<
            "Process" | "Risk",
            DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
        >
    ) => {
        if (this.state.drawer?.type === "Create") {
            const {
                riskDirectoryView,
                rootNode,
                common,
                entityList,
                createNode,
                lastAddedNodeId,
                updateRootNodeLinkedResources,
            } = this.props;
            const { parentId, nodeType } = this.state.drawer;
            const parentDesc = this.directoryDrawerNode(
                riskDirectoryView,
                rootNode,
                parentId
            );
            const dataView = this.directoryDataView(
                riskDirectoryView,
                rootNode
            );
            const lastAddedNode =
                lastAddedNodeId !== undefined
                    ? DV.find(
                          dataView.dataSource.iter(),
                          (desc) =>
                              dataView.dataSource.getId(desc) ===
                              lastAddedNodeId
                      )
                    : undefined;
            if (!parentDesc) {
                console.error(`Cannot find parent node ${parentId}`);
                this.setState({ drawer: undefined });
                return;
            }
            if (parentDesc.value.type === "Risk") {
                console.error(`Cannot add child to risk node ${parentId}`);
                this.setState({ drawer: undefined });
                return;
            }
            const metadataExtractors = nodeTypeToNodeExtractors[nodeType];
            const ancestorsEntities =
                common.getDirectoryAncestorsEntitiesWithNodePaths([
                    ...parentDesc.ancestors,
                    parentDesc.value,
                ]);
            const permission = this.nodeCreationPermission(
                riskDirectoryView,
                rootNode
            )[parentDesc.value.id];
            if (permission.type === "NoPermission") {
                throw new Error("Node creation is forbidden");
            }
            return (
                <CreateNodeDrawer
                    id={generate()}
                    onClose={this.handleCloseDrawer}
                    parentNode={parentDesc.value}
                    entities={common.entities(entityList, rootNode)}
                    lastAddedNodeScope={
                        lastAddedNode !== undefined
                            ? U.getScope(lastAddedNode.value)
                            : undefined
                    }
                    nodeType={nodeType}
                    ancestorsEntities={ancestorsEntities}
                    riskDirectoryView={riskDirectoryView}
                    metadataExtractors={metadataExtractors}
                    createNode={createNode}
                    permission={permission}
                    updateRootNodeLinkedResources={
                        updateRootNodeLinkedResources
                    }
                />
            );
        } else {
            return null;
        }
    };

    renderDialog = () => {
        const { dialog } = this.state;
        switch (dialog) {
            case undefined: {
                return null;
            }
            case "DeleteDirectory": {
                const { riskDirectoryView } = this.props;

                return (
                    <DeleteWithConfirmationDialog
                        title={t("RiskDirectory_View_Delete_Title")}
                        onConfirm={this.props.deleteDirectory}
                        onCancel={() => {
                            this.setState({ dialog: undefined });
                        }}
                    >
                        {renderMarkdown(
                            t("RiskDirectory_View_Delete_Body", {
                                name: riskDirectoryView.content.resource.title,
                            })
                        )}
                    </DeleteWithConfirmationDialog>
                );
            }
            case "CopyDirectory": {
                const { riskDirectoryView } = this.props;

                return (
                    <CopyWithConfirmationDialog
                        riskDirectory={riskDirectoryView}
                        onCancel={() => {
                            this.setState({ dialog: undefined });
                        }}
                    />
                );
            }
            default: {
                return assertNever(dialog);
            }
        }
    };

    private addFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChange) {
                onFilterChange(
                    addFieldFilter(extractorId, filter, excluded, oldFilter)
                );
            }
        };

    private removeFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChange) {
                onFilterChange(
                    oldFilter &&
                        removeFieldFilter(
                            extractorId,
                            filter,
                            excluded,
                            oldFilter
                        )
                );
            }
        };

    private resetFilter =
        (
            _oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        () => {
            if (onFilterChange) {
                onFilterChange(null);
            }
        };

    /**
     * Builds "elementary" metadata extractors, which can be used in the
     * drawer. They work on nodes, not node descriptors.
     */
    private buildNodeTypeToNodeExtractors = (
        riskDirectory: R.RiskDirectoryView,
        savedBlobs: SavedBlob[]
    ): Record<
        "Process" | "Risk",
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
    > => {
        const { responseEnumMap } = this.props;
        const formulaComputer = new FormulaComputer();
        const nodeTypeToExtractors: Record<
            R.NodeTypeWithMetadata,
            DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
        > = {
            Process: [],
            Risk: [],
        };
        const keys: ["Process", "Risk"] = ["Process", "Risk"];
        for (const nodeType of keys) {
            const fields = U.getRiskDirectoryLevelMDFields(
                riskDirectory,
                nodeType
            );
            nodeTypeToExtractors[nodeType] = DV.buildDataExtractors(
                fields.map((field) =>
                    U.buildNodeMetadataExtractorSpec(
                        field,
                        riskDirectory,
                        savedBlobs,
                        responseEnumMap,
                        formulaComputer
                    )
                )
            );
        }
        return nodeTypeToExtractors;
    };
    private buildDirectoryDataView = (
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto
    ): DV.DataView<DV.NodeDescriptor<T.DirectoryNode>> => {
        const { common } = this.props;
        // Works on nodes, not on node descriptors
        const nodeTypeToNodeExtractors = this.nodeTypeToNodeExtractors(
            riskDirectory,
            rootNode.linkedResources.blobs
        );
        // These extractors can work both on DirectoryNode and MapNode, so they can also work on just DirectoryNode
        const metadataExtractors = U.buildColumnMetadataExtractors(
            common.metadataColumns(riskDirectory).riskDirectoryLevel,
            nodeTypeToNodeExtractors
        ) as DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            T.MetadataInstance,
            IdAndName<MetadataColumnId, string>
        >[];

        // These extractors are for filtering purposes only, preventing unwanted filtering results with processes
        const filterMetadataExtractors = U.buildFilterMetadataExtractors(
            common.metadataColumns(riskDirectory),
            nodeTypeToNodeExtractors,
            false
        ) as DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            T.MetadataInstance,
            IdAndName<MetadataColumnId, string>
        >[];

        const dataSource: DV.DataSource<DV.NodeDescriptor<T.DirectoryNode>> =
            new DV.MultiTreeDataSource<T.DirectoryNode>(
                [rootNode.content.resource.rootNode],
                U.getDirectoryChildren,
                U.getId,
                (node) => node.content.name
            );
        const defaultCompareItems: Cmp.ComparisonFunction<
            DV.NodeDescriptor<T.DirectoryNode>
        > = Cmp.map((desc) => desc.index, Cmp.primitive);

        function unwrapDescriptor<V>(
            fn: (node: T.DirectoryNode) => V
        ): (desc: DV.NodeDescriptor<T.DirectoryNode>) => V {
            return (desc) => fn(desc.value);
        }

        const stringCompare = Cmp.string();

        const nodeIdToName: Record<RiskNodeId, string> = {};
        DV.forEach(dataSource.iter(), (desc) => {
            const node = desc.value;
            nodeIdToName[U.getNodeGuid(node)] = node.content.name;
        });

        const buildEntityExtractor = (): DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            EntityId[],
            IdAndName<EntityId, string>
        > => {
            const entityIdToName = this.entityIdToName();
            return {
                id: RISK_EXTRACTOR_KEYS.ENTITIES,
                label: t("RiskMaps_Edit_AssignedEntities"),
                getValue: common.getEntities,
                asString: common.renderEntities,
                compare: Cmp.map(common.renderEntities, stringCompare),
                customFilterTransformer: {
                    getValue: (_node) => {
                        throw new Error(
                            `Unexpected call of customFilterTransformer.getValue() on ${RISK_EXTRACTOR_KEYS.ENTITIES} extractor`
                        );
                    },
                    asString: ({ id, name }) => entityIdToName[id] || name,
                },
            };
        };

        const buildRiskExtractor = (): DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            string | undefined,
            IdAndName<RiskNodeId, string> | undefined
        > => {
            return {
                id: RISK_EXTRACTOR_KEYS.RISK,
                label: t("RiskMaps_Edit_EditTree_NodeMenu_Risk"),
                getValue: unwrapDescriptor(common.getRisk),
                asString: unwrapDescriptor(
                    (node) => common.getRisk(node) || ""
                ),
                compare: Cmp.map(
                    unwrapDescriptor(common.getRisk),
                    Cmp.undefinedLast(stringCompare)
                ),
                customFilterTransformer: {
                    getValue: unwrapDescriptor((node) => {
                        const name = common.getRisk(node);
                        if (name !== undefined) {
                            return { id: U.getNodeGuid(node), name };
                        } else {
                            return undefined;
                        }
                    }),
                    asString: (idAndName) =>
                        idAndName !== undefined
                            ? nodeIdToName[idAndName.id] || idAndName.name
                            : "",
                },
            };
        };

        const buildProcessesExtractor = (): DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            ProcessNode[],
            IdAndName<RiskNodeId, string>
        > => {
            return {
                id: RISK_EXTRACTOR_KEYS.PROCESSES,
                label: t("RiskMaps_Edit_EditTree_NodeMenu_Process"),
                getValue: common.getProcesses,
                asString: (desc) =>
                    common
                        .getProcesses(desc)
                        .map((process) => process.content.name)
                        .join(),
                compare: Cmp.map(
                    (desc) =>
                        common
                            .getProcesses(desc)
                            .map((process) => process.content.name),
                    (p1, p2) => Cmp.arrayCompare(p1, p2, stringCompare)
                ),
                customFilterTransformer: {
                    getValue: (_node) => {
                        throw new Error(
                            `Unexpected call of customFilterTransformer.getValue() on ${RISK_EXTRACTOR_KEYS.PROCESSES} extractor`
                        );
                    },
                    asString: ({ id, name }) => nodeIdToName[id] || name,
                },
            };
        };

        function buildNodeScopeExtractor(): DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode>,
            T.NodeScope,
            BinaryNodeScope | undefined
        > {
            return {
                id: RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
                label: t("RiskDirectory_Scope"),
                getValue: unwrapDescriptor(U.getFilterScope),
                asString: unwrapDescriptor((node) =>
                    U.localizeFilterScope(U.getFilterScope(node)?.scope)
                ),
                compare: Cmp.map(
                    unwrapDescriptor((node) =>
                        U.localizeFilterScope(U.getFilterScope(node)?.scope)
                    ),
                    stringCompare
                ),
                customFilterTransformer: {
                    getValue: unwrapDescriptor(
                        (node) => U.getFilterScope(node)?.scope
                    ),
                    asString: U.localizeFilterScope,
                },
            };
        }

        const extractorGroups: DV.DataExtractorGroup<
            DV.NodeDescriptor<T.DirectoryNode>
        >[] = [
            {
                id: "extractor-group",
                label: "Extractor group",
                extractors: [
                    {
                        id: RISK_EXTRACTOR_KEYS.PATH,
                        label: t("RiskMapResponse_Path"),
                        getValue: U.getDirectoryPath,
                        asString: U.getDirectoryPath,
                        compare: Cmp.map(U.getDirectoryPath, stringCompare),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.NAME,
                        label: t("RiskMaps_Edit_EditTree_NodeName"),
                        getValue: (desc) => desc.value.content.name,
                        asString: (desc) => desc.value.content.name,
                        compare: Cmp.map(
                            (desc) => desc.value.content.name,
                            stringCompare
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.DESCRIPTION,
                        label: t("RiskMaps_Edit_EditTree_NodeDescription"),
                        getValue: common.getDescription,
                        asString: common.getDescription,
                        compare: Cmp.map(common.getDescription, stringCompare),
                    },
                    ...metadataExtractors,
                    ...filterMetadataExtractors,
                    downgradeExtractorType(buildEntityExtractor()),
                    downgradeExtractorType(buildRiskExtractor()),
                    downgradeExtractorType(buildProcessesExtractor()),
                    {
                        id: RISK_EXTRACTOR_KEYS.SCOPE,
                        label: t("RiskDirectory_Scope"),
                        getValue: unwrapDescriptor(U.getScope),
                        asString: unwrapDescriptor((node) =>
                            U.localizeScope(U.getScope(node).scope)
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) =>
                                U.localizeScope(U.getScope(node).scope)
                            ),
                            stringCompare
                        ),
                    },
                    downgradeExtractorType(buildNodeScopeExtractor()),
                ],
            },
        ];
        const sorters: DV.Sorter[] = [];

        return {
            dataSource,
            defaultCompareItems,
            extractorGroups,
            sorters,
        };
    };
    private buildDirectoryColumnBuilders = (
        riskDirectory: R.RiskDirectoryView
    ): Record<
        string,
        | ColumnBuilder<DV.NodeDescriptor<T.DirectoryNode>, string>
        | Record<
              ModeType,
              ColumnBuilder<DV.NodeDescriptor<T.DirectoryNode>, string>
          >
    > => {
        const { common } = this.props;
        const metadataColumns: Record<
            string,
            ColumnBuilder<DV.NodeDescriptor<T.DirectoryNode>, string>
        > = {};
        const riskMetadataColumns = common
            .metadataColumns(riskDirectory)
            .riskDirectoryLevel.filter((col) => col.fields.Risk !== undefined);

        const defaultVisibleColumns = riskMetadataColumns
            .filter((col) =>
                Object.values(col.fields).every(
                    (f) => f !== undefined && f.fieldType.type !== "TextArea"
                )
            )
            .slice(0, 5)
            .map((col) => col.id);

        riskMetadataColumns
            .map((col) => ({
                id: col.id,
                builder: (): ColumnOptions<
                    DV.NodeDescriptor<T.DirectoryNode>
                > => {
                    const opts: ColumnOptions<
                        DV.NodeDescriptor<T.DirectoryNode>
                    > = {
                        display: defaultVisibleColumns.includes(col.id)
                            ? "DEFAULT_VISIBLE"
                            : "DEFAULT_HIDDEN",
                    };
                    return opts;
                },
            }))
            .forEach(({ id, builder }) => (metadataColumns[id] = builder));

        /// These columns are for filter purposes only, so we never display them.
        const filterMetadataColumns: Record<
            string,
            ColumnBuilder<DV.NodeDescriptor<T.DirectoryNode>, string>
        > = {};
        common
            .metadataColumns(riskDirectory)
            .riskDirectoryLevel.filter(
                (col) => col.fields.Process !== undefined
            )
            .forEach((col) => {
                filterMetadataColumns[
                    U.prefixFilterMetadataExtractor(col.id, "Process")
                ] = (): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                    display: "NEVER",
                });
            });
        common
            .metadataColumns(riskDirectory)
            .riskDirectoryLevel.filter((col) => col.fields.Risk !== undefined)
            .forEach((col) => {
                filterMetadataColumns[
                    U.prefixFilterMetadataExtractor(col.id, "Risk")
                ] = (): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                    display: "NEVER",
                });
            });

        return {
            path: {
                [Mode.TABLE]: (): ColumnOptions<
                    DV.NodeDescriptor<T.DirectoryNode>
                > => ({ display: "DEFAULT_VISIBLE" }),
                [Mode.TREE]: (): ColumnOptions<
                    DV.NodeDescriptor<T.DirectoryNode>
                > => ({
                    display: "NEVER",
                }),
            },
            name: {
                [Mode.TABLE]: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                    display: "ALWAYS",
                    getFormattedValue: (desc) => (
                        <NameColumn node={desc.value}>
                            {extractor.getValue(desc)}
                        </NameColumn>
                    ),
                }),
                [Mode.TREE]: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                    display: "ALWAYS",
                    getFormattedValue: (desc) => (
                        <NameColumn node={desc.value}>
                            {desc.value.type === "Risk" ? (
                                extractor.getValue(desc)
                            ) : (
                                <b>{extractor.getValue(desc)}</b>
                            )}
                        </NameColumn>
                    ),
                }),
            },
            description: (
                extractor
            ): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                display: "DEFAULT_VISIBLE",
                getFormattedValue: (desc) =>
                    renderMarkdown(extractor.getValue(desc) || ""),
            }),
            entities: (): ColumnOptions<
                DV.NodeDescriptor<T.DirectoryNode>
            > => ({
                display: "DEFAULT_VISIBLE",
            }),
            risk: (): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                display: "NEVER",
            }),
            processes: (): ColumnOptions<
                DV.NodeDescriptor<T.DirectoryNode>
            > => ({
                display: "NEVER",
            }),
            scope: (): ColumnOptions<DV.NodeDescriptor<T.DirectoryNode>> => ({
                display: "NEVER",
            }),
            filterScope: (): ColumnOptions<
                DV.NodeDescriptor<T.DirectoryNode>
            > => ({
                display: "NEVER",
            }),
            ...metadataColumns,
            ...filterMetadataColumns,
        };
    };

    private entityIdToName = (): Record<EntityId, string> => {
        const mapIdToName: Record<EntityId, string> = {};
        const { entityList, common, rootNode } = this.props;
        const entities = common.entities(entityList, rootNode);
        for (const id in entities) {
            if (Object.prototype.hasOwnProperty.call(entities, id)) {
                mapIdToName[id] = entities[id].name;
            }
        }
        return mapIdToName;
    };

    private buildDirectoryDataViewIndex = (
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        entityList: EntityOverviewListDto
    ): DataViewIndex<DV.NodeDescriptor<T.DirectoryNode>> => {
        const { common } = this.props;
        type Expander = DV.ExtractorExpander<
            T.MetadataInstance,
            T.DataViewIndexValue,
            T.MetadataValueForFilter
        >[];
        const dataView = this.directoryDataView(riskDirectory, rootNode);
        const metadataColumns = common
            .metadataColumns(riskDirectory)
            .riskDirectoryLevel.filter((col) =>
                Object.values(col.fields).every(
                    (f) => f !== undefined && f.fieldType.type !== "TextArea"
                )
            );
        const entities = common.entities(entityList, rootNode);
        const stringCompare = Cmp.string();
        const expand = (
            acc: Expander,
            f: R.MetadataField,
            id: string
        ): void => {
            switch (f.fieldType.type) {
                case "CheckboxGroup":
                    acc.push({
                        extractorId: id,
                        expand: (instance: T.MetadataInstance) => {
                            if (
                                instance !== undefined &&
                                instance.type !== "CheckboxGroupInstance"
                            ) {
                                throw new Error(
                                    "Expected CheckboxGroupInstance for field of type CheckboxGroup, got " +
                                        instance.type
                                );
                            }
                            return (
                                instance?.value?.map((opt) => ({
                                    partialId: opt.id,
                                    expandedValue: {
                                        type: "OptionValue",
                                        value: opt,
                                    },
                                })) ?? []
                            );
                        },
                        compare: Cmp.map(
                            ({ value }) => (value as R.OptionValue).value,
                            stringCompare
                        ), // Casting is safe because an error is thrown before if the type is wrong
                        asString: ({ value }) => (value as R.OptionValue).value, // Casting is safe, see above
                        customFilterTransformer: {
                            getValue: (dviValue) => {
                                if (dviValue.type !== "OptionValue") {
                                    throw new Error(
                                        "Expected OptionValue for field of type CheckboxGroup, got " +
                                            dviValue.type
                                    );
                                }
                                return {
                                    id: dviValue.value.id,
                                    name: dviValue.value.value,
                                };
                            },
                            asString: U.stringifyMetadataValueForFilter,
                        },
                    });
                    break;
                case "AttachedFiles":
                    acc.push({
                        extractorId: id,
                        expand: (instance: T.MetadataInstance) => {
                            if (
                                instance !== undefined &&
                                instance.type !== "AttachedFilesInstance"
                            ) {
                                throw new Error(
                                    "Expected AttachedFilesInstance for field of type AttachedFiles, got " +
                                        instance.type
                                );
                            }
                            return (
                                instance?.value?.map((blob) => ({
                                    partialId: blob.id,
                                    expandedValue: {
                                        type: "AttachedFile",
                                        value: blob,
                                    },
                                })) ?? []
                            );
                        },
                        compare: Cmp.map(
                            ({ value }) => (value as SavedBlob).fileName,
                            stringCompare
                        ), // Casting is safe because an error is thrown before if the type is wrong
                        asString: ({ value }) => (value as SavedBlob).fileName, // Casting is safe, see above
                        customFilterTransformer: {
                            getValue: (dviValue) => {
                                if (dviValue.type !== "AttachedFile") {
                                    throw new Error(
                                        "Expected AttachedFile for field of type AttachedFile, got " +
                                            dviValue.type
                                    );
                                }
                                return {
                                    id: dviValue.value.id,
                                    name: dviValue.value.fileName,
                                };
                            },
                            asString: U.stringifyMetadataValueForFilter,
                        },
                    });
                    break;
                case "TextInput":
                case "Heading":
                case "TextArea":
                case "NumericInput":
                case "Dropdown":
                case "RadioButtonGroup":
                    break;
                default:
                    assertNever(f.fieldType);
            }
        };
        const colIds: string[] = [];
        const expanders = metadataColumns.reduce(
            (acc: Expander, col): Expander => {
                if (
                    col.fields.Process !== undefined &&
                    col.fields.Process.fieldType.type !== "TextArea"
                ) {
                    const id = U.prefixFilterMetadataExtractor(
                        col.id,
                        "Process"
                    );
                    expand(acc, col.fields.Process, id);
                    colIds.push(id);
                }
                if (
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
                ) {
                    const id = U.prefixFilterMetadataExtractor(col.id, "Risk");
                    expand(acc, col.fields.Risk, id);
                    colIds.push(id);
                }
                return acc;
            },
            []
        );

        const nodeIdToName: Record<RiskNodeId, string> = {};
        DV.forEach(dataView.dataSource.iter(), (desc) => {
            const node = desc.value;
            nodeIdToName[U.getNodeGuid(node)] = node.content.name;
        });

        const entityIdToName = this.entityIdToName();
        return new DataViewIndex(
            dataView,
            [
                RISK_EXTRACTOR_KEYS.RISK,
                RISK_EXTRACTOR_KEYS.PROCESSES,
                ...colIds,
                RISK_EXTRACTOR_KEYS.ENTITIES,
                RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
            ],
            [
                {
                    extractorId: RISK_EXTRACTOR_KEYS.ENTITIES,
                    expand: (values: number[] | undefined) =>
                        (values || []).map((id) => ({
                            partialId: `entity-${id}`,
                            expandedValue: {
                                id,
                                value: entities[id].name,
                            },
                        })),
                    compare: Cmp.map(({ value }) => value, stringCompare),
                    asString: (_expandedValue) => {
                        throw new Error(
                            `Unexpected call of asString() on ${RISK_EXTRACTOR_KEYS.ENTITIES} expander`
                        );
                    },
                    customFilterTransformer: {
                        getValue: ({ id, value }) => ({ id, name: value }),
                        asString: ({ id, name }) => entityIdToName[id] || name,
                    },
                },
                {
                    extractorId: RISK_EXTRACTOR_KEYS.PROCESSES,
                    expand: (values: R.ProcessNode[]) =>
                        values.map((process) => ({
                            partialId: `process-${process.id}`,
                            expandedValue: {
                                id: process.id,
                                value: process.content.name,
                            },
                        })),
                    compare: Cmp.map(({ value }) => value, stringCompare),
                    asString: (_expandedValue) => {
                        throw new Error(
                            `Unexpected call of asString() on ${RISK_EXTRACTOR_KEYS.PROCESSES} expander`
                        );
                    },
                    customFilterTransformer: {
                        getValue: ({ id, value }) => ({ id, name: value }),
                        asString: ({ id, name }) => nodeIdToName[id] || name,
                    },
                },
                ...expanders,
            ]
        );
    };
    private buildDirectoryQueryResult = (
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        filter: SimpleAndFilter | null,
        entityList: EntityOverviewListDto
    ): QueryResult<DV.NodeDescriptor<T.DirectoryNode>> => {
        const dataViewIndex = this.directoryDataViewIndex(
            riskDirectory,
            rootNode,
            entityList
        );
        const queryResult = dataViewIndex.find(filter);
        const entitiesFilter = filter?.filters.find(
            (filter) => filter.extractorId === RISK_EXTRACTOR_KEYS.ENTITIES
        );
        if (
            entitiesFilter &&
            entitiesFilter.filters &&
            entitiesFilter.filters.length > 0
        ) {
            const entityQueryResult = this.entityDataViewIndex(entityList).find(
                {
                    filters: [
                        {
                            extractorId: ENTITY_EXTRACTOR_KEYS.ENTITY_NAME,
                            filters: entitiesFilter.filters,
                        },
                    ],
                }
            );
            const matchingEntityIds = new Set<number>();
            DV.forEach(entityQueryResult.dataView.dataSource.iter(), (entity) =>
                matchingEntityIds.add(entity.id)
            );
            const excludedIds = new Set<string>();
            const includedIds = new Set<string>();
            DV.forEach(queryResult.dataView.dataSource.iter(), (node) => {
                const nodeScope = U.getScope(node.value);
                if (
                    nodeScope.scope === "Local" &&
                    !matchingEntityIds.has(nodeScope.entityId)
                ) {
                    excludedIds.add(
                        queryResult.dataView.dataSource.getId(node)
                    );
                } else {
                    includedIds.add(
                        queryResult.dataView.dataSource.getId(node)
                    );
                }
            });
            return {
                ...queryResult,
                dataView: {
                    ...queryResult.dataView,
                    dataSource: queryResult.dataView.dataSource.filteredByIds(
                        excludedIds,
                        includedIds
                    ),
                },
            };
        }
        return queryResult;
    };
    private buildNodeCreationPermission = (
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto
    ): Record<Guid, T.NodeCreationPermission> => {
        const { common } = this.props;

        const nodePermissions: Record<Guid, T.NodeCreationPermission> = {};
        const recursiveAddPermissions = (
            node: R.RiskDirectoryNode | R.ProcessNode
        ) => {
            nodePermissions[node.id] = U.computeNodeCreationPermission(
                node,
                riskDirectory.content.actions,
                common
            );

            node.nodes.forEach((childNode) => {
                switch (childNode.type) {
                    case "Process":
                        recursiveAddPermissions(childNode);
                        break;
                    case "Risk":
                        // Nothing to do
                        break;
                    default:
                        assertNever(childNode);
                }
            });
        };

        recursiveAddPermissions(rootNode.content.resource.rootNode);

        return nodePermissions;
    };
    private buildEntityDataViewIndex = (
        entityList: EntityOverviewListDto
    ): DataViewIndex<EntityOverview> => {
        const dataView: DV.DataView<EntityOverview> = {
            dataSource: new DV.ArrayDataSource(
                entityList.content.map((content) => content.resource),
                (entity) => entity.id.toString()
            ),
            defaultCompareItems: Cmp.map((entity) => entity.id, Cmp.primitive),
            extractorGroups: [
                {
                    id: "extractor-group",
                    label: "Extractor group",
                    extractors: [
                        {
                            id: ENTITY_EXTRACTOR_KEYS.ENTITY_NAME,
                            label: "",
                            getValue: (entity) => entity.name,
                            asString: (entity) => entity.name,
                            compare: Cmp.map(
                                (entity) => entity.name,
                                Cmp.string()
                            ),
                            customFilterTransformer: {
                                getValue: (entity) => ({
                                    id: entity.id,
                                    name: entity.name,
                                }),
                                asString: ({ id, name }) =>
                                    entityList.content.find(
                                        (entity) => entity.resource.id === id
                                    )?.resource.name ?? name,
                            },
                        },
                    ],
                },
            ],
            sorters: [],
        };

        return new DataViewIndex<EntityOverview>(dataView, [
            ENTITY_EXTRACTOR_KEYS.ENTITY_NAME,
        ]);
    };

    private handleActiveNodeChanged = (
        desc: DV.NodeDescriptor<T.DirectoryNode>
    ) => {
        this.setState({
            drawer: { type: "View", nodeId: desc.value.id },
        });
    };
    private handleCloseDrawer = () => {
        this.setState({ drawer: undefined });
    };

    private canAddNode = (
        parentNode: R.RiskDirectoryNode | R.ProcessNode
    ): boolean => {
        const { riskDirectoryView, rootNode } = this.props;
        const permissions = this.nodeCreationPermission(
            riskDirectoryView,
            rootNode
        )[parentNode.id];
        return permissions.type !== "NoPermission";
    };

    private getAddNodeWidget = (
        parentNode: T.DirectoryNode
    ): React.ReactNode | null => {
        switch (parentNode.type) {
            case "RiskDirectory":
            case "Process":
                if (this.canAddNode(parentNode)) {
                    return (
                        <div className={theme.addChildHeightAdapter}>
                            <Dropdown
                                trigger={["click"]}
                                overlay={
                                    <Menu
                                        items={[
                                            {
                                                key: `add-child-${parentNode.id}-process`,
                                                className: theme.menuItem,
                                                onClick: () =>
                                                    this.createNode(
                                                        parentNode,
                                                        "Process"
                                                    ),
                                                label: t(
                                                    "RiskDirectory_NodeCreate_NewProcess"
                                                ),
                                            },
                                            {
                                                key: `add-child-${parentNode.id}-risk`,
                                                className: theme.menuItem,
                                                onClick: () =>
                                                    this.createNode(
                                                        parentNode,
                                                        "Risk"
                                                    ),
                                                label: t(
                                                    "RiskDirectory_NodeCreate_NewRisk"
                                                ),
                                            },
                                        ]}
                                    />
                                }
                            >
                                <Button
                                    id={`add-child-${parentNode.id}`}
                                    className={theme.addChild}
                                    variant="link"
                                >
                                    <>
                                        {t("RiskMapEditStructure_AddButton")}
                                        <DownOutlined />
                                    </>
                                </Button>
                            </Dropdown>
                        </div>
                    );
                } else {
                    return null;
                }
            case "Risk":
                return null;
            default:
                return assertNever(parentNode);
        }
    };

    private createNode = (
        parentNode: R.RiskDirectoryNode | R.ProcessNode,
        type: "Process" | "Risk"
    ) => {
        this.setState({
            drawer: { type: "Create", parentId: parentNode.id, nodeType: type },
        });
    };
}

interface DirectoryTabProps extends T.CommonProps {
    common: T.CommonGetters;
    responseEnumMap: EnumMap<R.ResponseEnum>;
    deleteDirectory: () => void;
    updateNode: (
        action: T.NodeUpdateAction<T.DirectoryNode>,
        targetNode: T.DirectoryNode
    ) => Promise<void>;
    onDeleteNode: (node: T.DirectoryNode) => void;
    createNode: (newNode: R.NewRiskDirectoryNode) => Promise<void>;
    lastAddedNodeId: Guid | undefined;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

interface DirectoryViewState {
    dialog: "DeleteDirectory" | "CopyDirectory" | undefined;
    drawer: DirectoryViewDrawer | NodeCreateDrawer | undefined;
}

interface DirectoryViewDrawer {
    type: "View";
    nodeId: Guid;
}

interface NodeCreateDrawer {
    type: "Create";
    parentId: Guid;
    nodeType: "Process" | "Risk";
}
